import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Card,
  Row,
  Col,
  Pagination,
  InputGroup,
} from "react-bootstrap";
import {
  FaEye,
  FaCheck,
  FaTimes,
  FaPlus,
  FaSearch,
  FaFilter,
  FaFileAlt,
  FaEdit,
  FaTrash,
  FaFileExcel,
} from "react-icons/fa";
import { gastosService } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

const GastosList = () => {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [gastos, setGastos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [tiposGastos, setTiposGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 8,
    total: 0,
    pages: 0,
  });

  // Filtros según el stored procedure
  const [filters, setFilters] = useState({
    search: "",
    validado: "",
    tipo_gasto_id: "",
    empleado_id: "",
    fecha_desde: "",
    fecha_hasta: "",
    orderBy: 0,
  });

  // Estado visual para los campos de fecha (no dispara consultas)
  const [dateInputs, setDateInputs] = useState({
    fecha_desde: "",
    fecha_hasta: "",
  });

  // Estado para el valor temporal del buscador
  const [searchValue, setSearchValue] = useState("");

  const [formData, setFormData] = useState({
    monto: "",
    descripcion: "",
    fecha_gasto: "",
    tipo_gasto_id: "",
    imagen_url: "",
  });

  const [statusData, setStatusData] = useState({
    validado: false,
    observaciones: "",
  });

  // Estados para selección múltiple
  const [selectedGastos, setSelectedGastos] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmIndividualModal, setShowConfirmIndividualModal] =
    useState(false);
  const [gastoToValidate, setGastoToValidate] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gastoToDelete, setGastoToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [gastoToEdit, setGastoToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    precioTiquet: "",
    precioTarifa: "",
    tipoGastoId: "",
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportConfirmModal, setShowExportConfirmModal] = useState(false);
  const [exportData, setExportData] = useState(null);

  // Debug: Mostrar información del usuario y permisos
  console.log("GastosList - Información del usuario:", {
    user: user,
    isAdmin: isAdmin,
    userRol: user?.ROL,
  });

  // Debounce para la búsqueda
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          handleFilterChange("search", value);
        }, 500); // 500ms de delay
      };
    })(),
    []
  );

  // Debounce para las fechas
  const debouncedDateChange = useCallback(
    (() => {
      let timeoutId;
      return (field, value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          handleFilterChange(field, value);
        }, 1000); // 1 segundo de delay para fechas
      };
    })(),
    []
  );

  useEffect(() => {
    loadData();
  }, [pagination.page, filters]);

  // useEffect para cargar datos estáticos
  useEffect(() => {
    if (user) loadStaticData();
  }, [user]);

  // Sincronizar observaciones del gasto seleccionado al abrir modal
  useEffect(() => {
    if (showStatusModal && selectedGasto) {
      setStatusData((prev) => ({
        ...prev,
        observaciones: selectedGasto.OBSERVACIONES || "",
      }));
    }
  }, [showStatusModal, selectedGasto]);

  // Inicializar el valor del buscador
  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  // Inicializar los valores de fecha
  useEffect(() => {
    setDateInputs({
      fecha_desde: filters.fecha_desde,
      fecha_hasta: filters.fecha_hasta,
    });
  }, [filters.fecha_desde, filters.fecha_hasta]);

  // Seleccionar automáticamente gastos no validados
  useEffect(() => {
    if (gastos.length > 0) {
      const noValidadosIds = gastos
        .filter((gasto) => !gasto.VALIDADO)
        .map((gasto) => gasto.ID);
      setSelectedGastos(new Set(noValidadosIds));

      // Debug: Mostrar información de gastos y selección
      console.log("GastosList - Información de gastos:", {
        totalGastos: gastos.length,
        gastosNoValidados: gastos.filter((g) => !g.VALIDADO).length,
        gastosValidados: gastos.filter((g) => g.VALIDADO).length,
        noValidadosIds: noValidadosIds,
        selectedGastosSize: noValidadosIds.length,
      });
    }
  }, [gastos]);

  // Leer empleado_id de la URL al montar y cuando empleados esté cargado
  useEffect(() => {
    if (user && user.ROL === "admin" && empleados.length > 0) {
      const params = new URLSearchParams(location.search);
      const empleadoIdParam = params.get("empleado_id");
      if (
        empleadoIdParam &&
        empleados.some((emp) => String(emp.id) === String(empleadoIdParam))
      ) {
        setFilters((prev) => ({ ...prev, empleado_id: empleadoIdParam }));
      }
    }
    // eslint-disable-next-line
  }, [user, empleados]);

  const loadStaticData = async () => {
    try {
      setLoading(true);
      // Cargar tipos de gastos
      const tiposResponse = await gastosService.getTipos();
      setTiposGastos(tiposResponse.data);

      // Cargar empleados si es admin
      if (user && user.ROL === "admin") {
        const empleadosResponse = await gastosService.getEmpleados();
        setEmpleados(empleadosResponse.data);
      }
    } catch (error) {
      toast.error("Error al cargar datos estáticos");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setTableLoading(true);

      // Cargar solo gastos con filtros usando el stored procedure
      const gastosResponse = await gastosService.getAll({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });

      setGastos(gastosResponse.data.gastos);
      setPagination((prev) => ({
        ...prev,
        ...gastosResponse.data.pagination,
      }));
    } catch (error) {
      toast.error("Error al cargar gastos");
      console.error("Error:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Manejar cambios en el campo de búsqueda
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  // Manejar búsqueda con Enter
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleFilterChange("search", searchValue);
    }
  };

  // Manejar clic en el botón de búsqueda
  const handleSearchClick = () => {
    handleFilterChange("search", searchValue);
  };

  // Funciones para selección múltiple
  const handleSelectAll = (checked) => {
    if (checked) {
      const noValidadosIds = gastos
        .filter((gasto) => !gasto.VALIDADO)
        .map((gasto) => gasto.ID);
      setSelectedGastos(new Set(noValidadosIds));
    } else {
      setSelectedGastos(new Set());
    }
  };

  const handleSelectGasto = (gastoId, checked) => {
    const newSelected = new Set(selectedGastos);
    if (checked) {
      newSelected.add(gastoId);
    } else {
      newSelected.delete(gastoId);
    }
    setSelectedGastos(newSelected);
  };

  // Nueva función para manejar click en fila completa
  const handleRowClick = (gasto) => {
    if (!gasto.VALIDADO) {
      const isSelected = selectedGastos.has(gasto.ID);
      handleSelectGasto(gasto.ID, !isSelected);
    }
  };

  // Función para obtener la clase CSS de la fila según su estado
  const getRowClassName = (gasto) => {
    let className = "";
    if (!gasto.VALIDADO) {
      className += "table-danger ";
    }
    if (selectedGastos.has(gasto.ID)) {
      className += "row-selected ";
    }
    const finalClassName = className.trim();
    console.log(
      `Gasto ${gasto.ID}: VALIDADO=${
        gasto.VALIDADO
      }, selected=${selectedGastos.has(
        gasto.ID
      )}, className="${finalClassName}"`
    );
    return finalClassName;
  };

  const handleValidarSeleccionados = async () => {
    try {
      setTableLoading(true);

      // Validar todos los gastos seleccionados usando el stored procedure
      const promises = Array.from(selectedGastos).map((gastoId) =>
        gastosService.updateStatus(gastoId, {
          validado: true,
          observaciones: "Validación masiva",
        })
      );

      await Promise.all(promises);

      toast.success(`${selectedGastos.size} gastos validados exitosamente`);
      setSelectedGastos(new Set());
      setShowConfirmModal(false);
      loadData();
      // Disparar refresco de contadores en navbar
      window.dispatchEvent(new Event("refreshPendingCounts"));
    } catch (error) {
      toast.error("Error al validar gastos seleccionados");
      console.error("Error:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleValidarGastoIndividual = async (gastoId) => {
    try {
      setTableLoading(true);
      await gastosService.updateStatus(gastoId, {
        validado: true,
        observaciones: "Validación individual",
      });
      toast.success("Gasto validado exitosamente");
      setShowConfirmIndividualModal(false);
      setGastoToValidate(null);
      loadData();
      // Disparar refresco de contadores en navbar
      window.dispatchEvent(new Event("refreshPendingCounts"));
    } catch (error) {
      toast.error("Error al validar gasto");
      console.error("Error:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleConfirmarValidacionIndividual = (gasto) => {
    setGastoToValidate(gasto);
    setShowConfirmIndividualModal(true);
  };

  const handleVerTicket = (gasto) => {
    setSelectedTicket(gasto);
    setShowTicketModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedGasto(null);
    setStatusData({ validado: false, observaciones: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await gastosService.create(formData);
      toast.success("Gasto creado exitosamente");
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error al crear gasto");
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await gastosService.updateStatus(selectedGasto.ID, statusData);
      toast.success("Estado actualizado exitosamente");
      closeStatusModal();
      loadData();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const resetForm = () => {
    setFormData({
      monto: "",
      descripcion: "",
      fecha_gasto: "",
      tipo_gasto_id: "",
      imagen_url: "",
    });
  };

  const getStatusBadge = (validado) => {
    return validado ? (
      <Badge bg="success">Validado</Badge>
    ) : (
      <Badge bg="warning">Pendiente</Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("es-ES");
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("es-ES") +
      " " +
      date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const orderByOptions = [
    { value: 0, label: "Fecha Tiquet (Desc)" },
    { value: 1, label: "Fecha Tiquet (Asc)" },
    { value: 2, label: "Fecha Tiquet (Desc)" },
    { value: 3, label: "Empleado (Asc)" },
    { value: 4, label: "Empleado (Desc)" },
    { value: 5, label: "Evento (Asc)" },
    { value: 6, label: "Evento (Desc)" },
    { value: 7, label: "Inicio evento (Asc)" },
    { value: 8, label: "Inicio evento (Desc)" },
    { value: 9, label: "Fin evento (Asc)" },
    { value: 10, label: "Fin evento (Desc)" },
    { value: 11, label: "Precio Tiquet (Asc)" },
    { value: 12, label: "Precio Tiquet (Desc)" },
    { value: 13, label: "Precio Tarifa (Asc)" },
    { value: 14, label: "Precio Tarifa (Desc)" },
    { value: 15, label: "Validado (Asc)" },
    { value: 16, label: "Validado (Desc)" },
  ];

  const handleDeleteGasto = async (gastoId) => {
    try {
      setTableLoading(true);
      console.log("Gasto a eliminar:", gastoId);
      await gastosService.delete(gastoId);
      toast.success("Gasto eliminado correctamente");
      setShowDeleteModal(false);
      setGastoToDelete(null);
      loadData();
      // Disparar refresco de contadores en navbar
      window.dispatchEvent(new Event("refreshPendingCounts"));
    } catch (error) {
      toast.error("Error al eliminar gasto");
    } finally {
      setTableLoading(false);
    }
  };

  const handleEditClick = (gasto) => {
    setGastoToEdit(gasto);
    setEditForm({
      precioTiquet: gasto.PRECIO_ENVIADO,
      precioTarifa: gasto.PRECIO_REAL,
      tipoGastoId: gasto.GASTOS_TIPO_ID || gasto.tipo_gasto_id || "",
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditConfirm = async () => {
    try {
      setTableLoading(true);
      await gastosService.updateGasto(gastoToEdit.ID, {
        precioTiquet: editForm.precioTiquet,
        precioTarifa: editForm.precioTarifa,
        tipoGastoId: editForm.tipoGastoId,
      });
      toast.success("Gasto actualizado correctamente");
      setShowEditModal(false);
      setGastoToEdit(null);
      loadData();
    } catch (error) {
      toast.error("Error al actualizar gasto");
    } finally {
      setTableLoading(false);
    }
  };

  const handleExportExcel = async () => {
    // Validar que las fechas estén rellenadas
    if (!filters.fecha_desde || !filters.fecha_hasta) {
      toast.error("Debe seleccionar las fechas de inicio y fin para exportar");
      return;
    }

    try {
      setTableLoading(true);

      // Verificar si todos los gastos están aprobados en el rango de fechas
      const gastosResponse = await gastosService.getAll({
        ...filters,
        fecha_desde: filters.fecha_desde,
        fecha_hasta: filters.fecha_hasta,
        page: 1,
        limit: 1000, // Obtener todos los gastos del rango
      });

      const gastosEnRango = gastosResponse.data.gastos;
      const gastosNoAprobados = gastosEnRango.filter(
        (gasto) => !gasto.VALIDADO
      );

      if (gastosNoAprobados.length > 0) {
        setExportData({
          fechaDesde: filters.fecha_desde,
          fechaHasta: filters.fecha_hasta,
          gastosNoAprobados: gastosNoAprobados.length,
          totalGastos: gastosEnRango.length,
        });
        setShowExportConfirmModal(true);
        return;
      }

      // Si todos están aprobados, proceder con la exportación
      await exportarExcel(filters.fecha_desde, filters.fecha_hasta);
    } catch (error) {
      toast.error("Error al verificar gastos para exportación");
      console.error("Error:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleExportConfirm = async () => {
    try {
      setTableLoading(true);
      await exportarExcel(exportData.fechaDesde, exportData.fechaHasta);
      setShowExportConfirmModal(false);
      setExportData(null);
    } catch (error) {
      toast.error("Error al exportar Excel");
    } finally {
      setTableLoading(false);
    }
  };

  const exportarExcel = async (fechaDesde, fechaHasta) => {
    try {
      // Debug: verificar token
      const token = localStorage.getItem("token");
      console.log("Token presente:", !!token);
      console.log(
        "Token:",
        token ? token.substring(0, 20) + "..." : "No hay token"
      );

      const response = await gastosService.exportarExcel(
        fechaDesde,
        fechaHasta
      );

      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `gastos_${fechaDesde}_${fechaHasta}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Excel exportado correctamente");
    } catch (error) {
      console.error("Error completo:", error);
      toast.error("Error al generar el Excel");
      throw error;
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Cargando filtros...</div>;
  }

  return (
    <div>
      {/* Filtros */}
      <Card className="filter-section mb-4">
        <Card.Header>
          <h6 className="mb-0">
            <FaFilter /> Filtros
          </h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Buscar</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar en evento o tipo de gasto..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyPress}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={handleSearchClick}
                    title="Buscar"
                  >
                    <FaSearch />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            {user && user.ROL === "admin" && (
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Trabajador</Form.Label>
                  <Form.Select
                    value={filters.empleado_id}
                    onChange={(e) =>
                      handleFilterChange("empleado_id", e.target.value)
                    }
                  >
                    <option value="">Todos</option>
                    {empleados.map((empleado) => (
                      <option key={empleado.id} value={empleado.id}>
                        {empleado.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Gasto</Form.Label>
                <Form.Select
                  value={filters.tipo_gasto_id}
                  onChange={(e) =>
                    handleFilterChange("tipo_gasto_id", e.target.value)
                  }
                >
                  <option value="">Todos</option>
                  {tiposGastos.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Validado</Form.Label>
                <Form.Select
                  value={filters.validado}
                  onChange={(e) =>
                    handleFilterChange("validado", e.target.value)
                  }
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Ordenar Por</Form.Label>
                <Form.Select
                  value={filters.orderBy}
                  onChange={(e) =>
                    handleFilterChange("orderBy", e.target.value)
                  }
                >
                  {orderByOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={dateInputs.fecha_desde}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDateInputs((prev) => ({ ...prev, fecha_desde: value }));
                    if (value && value.length === 10) {
                      // Solo si la fecha está completa (YYYY-MM-DD)
                      debouncedDateChange("fecha_desde", value);
                    }
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Fecha Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={dateInputs.fecha_hasta}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDateInputs((prev) => ({ ...prev, fecha_hasta: value }));
                    if (value && value.length === 10) {
                      // Solo si la fecha está completa (YYYY-MM-DD)
                      debouncedDateChange("fecha_hasta", value);
                    }
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de Gastos */}
      <Card>
        <Card.Header className="d-flex flex-wrap align-items-center">
          <div className="col-12 col-md-auto mb-2 mb-md-0">
            <h5 className="mb-0">Lista de Gastos</h5>
          </div>
          <div className="col-12 col-md-auto d-flex flex-column flex-md-row gap-2 gap-md-2 ms-md-auto">
            <Button
              variant="outline-success"
              className="mb-2 mb-md-0"
              onClick={handleExportExcel}
              disabled={tableLoading}
              style={{ width: "100%" }}
            >
              <FaFileExcel /> Exportar Excel
            </Button>
            {selectedGastos.size > 0 && (
              <Button
                variant="success"
                onClick={() => setShowConfirmModal(true)}
                disabled={tableLoading}
                style={{ width: "100%" }}
              >
                <FaCheck /> Validar Seleccionados ({selectedGastos.size})
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {/* Vista de tabla para desktop */}
          <div className="d-none d-md-block">
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={
                        gastos.filter((g) => !g.VALIDADO).length > 0 &&
                        gastos
                          .filter((g) => !g.VALIDADO)
                          .every((g) => selectedGastos.has(g.ID))
                      }
                      indeterminate={
                        gastos.filter((g) => !g.VALIDADO).length > 0 &&
                        gastos
                          .filter((g) => !g.VALIDADO)
                          .some((g) => selectedGastos.has(g.ID)) &&
                        !gastos
                          .filter((g) => !g.VALIDADO)
                          .every((g) => selectedGastos.has(g.ID))
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={tableLoading}
                    />
                  </th>
                  {user && user.ROL === "admin" && <th>Trabajador</th>}
                  <th>Fecha</th>
                  <th>Evento</th>
                  <th>Inicio evento</th>
                  <th>Fin evento</th>
                  <th>Tipo</th>
                  <th>Precio Tiquet</th>
                  <th>Precio Tarifa</th>
                  <th>Validado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td
                      colSpan={user && user.ROL === "admin" ? 11 : 10}
                      className="text-center py-4"
                    >
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      Cargando gastos...
                    </td>
                  </tr>
                ) : gastos.length === 0 ? (
                  <tr>
                    <td
                      colSpan={user && user.ROL === "admin" ? 11 : 10}
                      className="text-center py-4"
                    >
                      No se encontraron gastos
                    </td>
                  </tr>
                ) : (
                  gastos.map((gasto) => (
                    <tr
                      key={gasto.ID}
                      className={getRowClassName(gasto)}
                      onClick={() => handleRowClick(gasto)}
                    >
                      <td>
                        {!gasto.VALIDADO && (
                          <Form.Check
                            type="checkbox"
                            checked={selectedGastos.has(gasto.ID)}
                            onChange={(e) =>
                              handleSelectGasto(gasto.ID, e.target.checked)
                            }
                            disabled={tableLoading}
                          />
                        )}
                      </td>
                      {user && user.ROL === "admin" && (
                        <td>{gasto.EMPLEADO}</td>
                      )}
                      <td>{formatDate(gasto.FECHA_TIQUET)}</td>
                      <td>{gasto.EVENTO}</td>
                      <td>{formatDateTime(gasto.PLANPERIOD_START)}</td>
                      <td>{formatDateTime(gasto.PLANPERIOD_END)}</td>
                      <td>{gasto.TIPO_GASTO}</td>
                      <td>{formatCurrency(gasto.PRECIO_ENVIADO)}</td>
                      <td>{formatCurrency(gasto.PRECIO_REAL)}</td>
                      <td>{getStatusBadge(gasto.VALIDADO)}</td>
                      <td>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedGasto(gasto);
                            setShowStatusModal(true);
                          }}
                        >
                          <FaEye />
                        </Button>
                        {gasto.IMAGEN && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerTicket(gasto);
                            }}
                            title="Ver Tiquet"
                          >
                            <FaFileAlt />
                          </Button>
                        )}
                        {!gasto.VALIDADO && (
                          <>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="me-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmarValidacionIndividual(gasto);
                              }}
                              disabled={tableLoading}
                            >
                              <FaCheck />
                            </Button>
                            {user && user.ROL === "admin" && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(gasto);
                                }}
                                title="Editar"
                              >
                                <FaEdit />
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="me-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setGastoToDelete(gasto);
                                setShowDeleteModal(true);
                              }}
                              title="Eliminar"
                            >
                              <FaTrash />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Vista de cards para móvil */}
          <div className="d-md-none">
            {tableLoading ? (
              <div className="text-center py-4">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Cargando...</span>
                </div>
                Cargando gastos...
              </div>
            ) : gastos.length === 0 ? (
              <div className="text-center py-4">No se encontraron gastos</div>
            ) : (
              <div className="gastos-cards">
                {gastos.map((gasto) => (
                  <Card
                    key={gasto.ID}
                    className={`mb-3 gasto-card ${getRowClassName(gasto)}`}
                    onClick={() => handleRowClick(gasto)}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{gasto.EVENTO}</h6>
                          <small className="text-muted">
                            {formatDate(gasto.FECHA_TIQUET)}
                          </small>
                        </div>
                        <div className="d-flex align-items-center">
                          {!gasto.VALIDADO && user && user.ROL === "admin" && (
                            <Form.Check
                              type="checkbox"
                              checked={selectedGastos.has(gasto.ID)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectGasto(gasto.ID, e.target.checked);
                              }}
                              disabled={tableLoading}
                              className="me-2"
                            />
                          )}
                          {getStatusBadge(gasto.VALIDADO)}
                        </div>
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <small className="text-muted d-block">Tipo:</small>
                          <span>{gasto.TIPO_GASTO}</span>
                        </div>
                        {user && user.ROL === "admin" && (
                          <div className="col-6">
                            <small className="text-muted d-block">
                              Trabajador:
                            </small>
                            <span>{gasto.EMPLEADO}</span>
                          </div>
                        )}
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <small className="text-muted d-block">
                            Precio Tiquet:
                          </small>
                          <span className="fw-bold">
                            {formatCurrency(gasto.PRECIO_ENVIADO)}
                          </span>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">
                            Precio Tarifa:
                          </small>
                          <span className="fw-bold">
                            {formatCurrency(gasto.PRECIO_REAL)}
                          </span>
                        </div>
                      </div>

                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <small className="text-muted d-block">Inicio evento:</small>
                          <span>{formatDateTime(gasto.PLANPERIOD_START)}</span>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Fin evento:</small>
                          <span>{formatDateTime(gasto.PLANPERIOD_END)}</span>
                        </div>
                      </div>

                      <div className="d-flex flex-wrap gap-1">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedGasto(gasto);
                            setShowStatusModal(true);
                          }}
                        >
                          <FaEye />
                        </Button>
                        {gasto.IMAGEN && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerTicket(gasto);
                            }}
                            title="Ver Tiquet"
                          >
                            <FaFileAlt />
                          </Button>
                        )}
                        {!gasto.VALIDADO && (
                          <>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmarValidacionIndividual(gasto);
                              }}
                              disabled={tableLoading}
                            >
                              <FaCheck />
                            </Button>
                            {user && user.ROL === "admin" && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(gasto);
                                }}
                                title="Editar"
                              >
                                <FaEdit />
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setGastoToDelete(gasto);
                                setShowDeleteModal(true);
                              }}
                              title="Eliminar"
                            >
                              <FaTrash />
                            </Button>
                          </>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Paginación */}
          {pagination.pages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: 1 }))
                  }
                  disabled={pagination.page === 1}
                />
                <Pagination.Prev
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                />
                {/* Páginas dinámicas centradas en la actual */}
                {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                  .filter(
                    (pageNum) =>
                      pageNum === 1 ||
                      pageNum === pagination.pages ||
                      (pageNum >= pagination.page - 2 &&
                        pageNum <= pagination.page + 2)
                  )
                  .map((pageNum, idx, arr) => {
                    // Añadir puntos suspensivos si hay salto
                    if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                      return [
                        <Pagination.Ellipsis
                          key={`ellipsis-${pageNum}`}
                          disabled
                        />,
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() =>
                            setPagination((prev) => ({
                              ...prev,
                              page: pageNum,
                            }))
                          }
                        >
                          {pageNum}
                        </Pagination.Item>,
                      ];
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() =>
                          setPagination((prev) => ({ ...prev, page: pageNum }))
                        }
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                <Pagination.Next
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: Math.min(pagination.pages, prev.page + 1),
                    }))
                  }
                  disabled={pagination.page === pagination.pages}
                />
                <Pagination.Last
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: pagination.pages,
                    }))
                  }
                  disabled={pagination.page === pagination.pages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal para crear gasto */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nuevo Gasto</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Monto</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.monto}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        monto: e.target.value,
                      }))
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Gasto</Form.Label>
                  <Form.Select
                    value={formData.tipo_gasto_id}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        tipo_gasto_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Seleccionar tipo</option>
                    {tiposGastos.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha del Gasto</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.fecha_gasto}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        fecha_gasto: e.target.value,
                      }))
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>URL de Imagen (opcional)</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.imagen_url}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        imagen_url: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    descripcion: e.target.value,
                  }))
                }
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Crear Gasto
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para ver/actualizar estado */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Detalles del Gasto</Modal.Title>
        </Modal.Header>
        {selectedGasto && (
          <>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>ID:</strong> {selectedGasto.ID}
                  </p>
                  <p>
                    <strong>Empleado:</strong> {selectedGasto.EMPLEADO}
                  </p>
                  <p>
                    <strong>Evento:</strong> {selectedGasto.EVENTO}
                  </p>
                  <p>
                    <strong>Tipo:</strong> {selectedGasto.TIPO_GASTO}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Fecha:</strong>{" "}
                    {formatDate(selectedGasto.FECHA_TIQUET)}
                  </p>
                  <p>
                    <strong>Precio Tiquet:</strong>{" "}
                    {formatCurrency(selectedGasto.PRECIO_ENVIADO)}
                  </p>
                  <p>
                    <strong>Precio Tarifa:</strong>{" "}
                    {formatCurrency(selectedGasto.PRECIO_REAL)}
                  </p>
                  <p>
                    <strong>Validado:</strong>{" "}
                    {getStatusBadge(selectedGasto.VALIDADO)}
                  </p>
                </Col>
              </Row>
              {selectedGasto.OBSERVACIONES && (
                <p>
                  <strong>Observaciones:</strong> {selectedGasto.OBSERVACIONES}
                </p>
              )}
              {user && !selectedGasto.VALIDADO && (
                <Form onSubmit={handleStatusUpdate}>
                  <Form.Group className="mb-3">
                    <Form.Label>Observaciones</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={statusData.observaciones}
                      onChange={(e) =>
                        setStatusData((prev) => ({
                          ...prev,
                          observaciones: e.target.value,
                        }))
                      }
                    />
                  </Form.Group>
                </Form>
              )}
            </Modal.Body>
            <Modal.Footer>
            <Button variant="secondary" onClick={closeStatusModal}>
                Cerrar
              </Button>
              {user && !selectedGasto.VALIDADO && (
                <Button variant="primary" onClick={handleStatusUpdate}>
                  Actualizar Estado
                </Button>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Modal de confirmación para validación masiva */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Validación Masiva</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <FaCheck className="text-success" style={{ fontSize: "3rem" }} />
            </div>
            <h5>¿Estás seguro?</h5>
            <p>
              Se van a validar <strong>{selectedGastos.size} gastos</strong>{" "}
              seleccionados.
            </p>
            <p className="text-danger">
              <strong>⚠️ Esta acción no se podrá revertir.</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleValidarSeleccionados}
            disabled={tableLoading}
          >
            {tableLoading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Validando...</span>
                </div>
                Validando...
              </>
            ) : (
              <>
                <FaCheck /> Confirmar Validación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación para validación individual */}
      <Modal
        show={showConfirmIndividualModal}
        onHide={() => setShowConfirmIndividualModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Validación Individual</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <FaCheck className="text-success" style={{ fontSize: "3rem" }} />
            </div>
            <h5>¿Estás seguro?</h5>
            <p>Se va a validar el gasto seleccionado.</p>
            <p className="text-danger">
              <strong>⚠️ Esta acción no se podrá revertir.</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmIndividualModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => handleValidarGastoIndividual(gastoToValidate.ID)}
            disabled={tableLoading}
          >
            {tableLoading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Validando...</span>
                </div>
                Validando...
              </>
            ) : (
              <>
                <FaCheck /> Confirmar Validación
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para ver tiquet */}
      <Modal
        show={showTicketModal}
        onHide={() => setShowTicketModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileAlt className="me-2" />
            Tiquet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTicket && selectedTicket.IMAGEN && (
            <div className="text-center">
              <div
                style={{
                  height: "60vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                {selectedTicket.IMAGEN.toLowerCase().endsWith(".pdf") ? (
                  <iframe
                    src={selectedTicket.IMAGEN}
                    width="100%"
                    height="100%"
                    style={{ border: "none", borderRadius: "4px" }}
                    title="Tiquet PDF"
                  />
                ) : (
                  <img
                    src={selectedTicket.IMAGEN}
                    alt="Tiquet"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "4px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                )}
              </div>
              <div className="mt-3">
                <small className="text-muted">
                  <strong>Evento:</strong> {selectedTicket.EVENTO} |
                  <strong> Fecha:</strong>{" "}
                  {formatDate(selectedTicket.FECHA_TIQUET)} |
                  <strong> Precio:</strong>{" "}
                  {formatCurrency(selectedTicket.PRECIO_ENVIADO)}
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTicketModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación para eliminar gasto */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <FaTrash className="text-danger" style={{ fontSize: "3rem" }} />
            </div>
            <h5>¿Estás seguro que quieres eliminar este gasto?</h5>
            <p className="text-danger">
              <strong>Esta acción no se podrá revertir.</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDeleteGasto(gastoToDelete.ID)}
            disabled={tableLoading}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de edición de gasto */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Gasto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gastoToEdit && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ID</Form.Label>
                    <Form.Control value={gastoToEdit.ID} disabled />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Empleado</Form.Label>
                    <Form.Control value={gastoToEdit.EMPLEADO} disabled />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Evento</Form.Label>
                    <Form.Control value={gastoToEdit.EVENTO} disabled />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fecha</Form.Label>
                    <Form.Control
                      value={formatDate(gastoToEdit.FECHA_TIQUET)}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Precio Tiquet</Form.Label>
                    <Form.Control
                      type="number"
                      name="precioTiquet"
                      value={editForm.precioTiquet}
                      onChange={handleEditFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Precio Tarifa</Form.Label>
                    <Form.Control
                      type="number"
                      name="precioTarifa"
                      value={editForm.precioTarifa}
                      onChange={handleEditFormChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tipo</Form.Label>
                    <Form.Select
                      name="tipoGastoId"
                      value={editForm.tipoGastoId}
                      onChange={handleEditFormChange}
                      required
                    >
                      <option value="">Seleccionar tipo</option>
                      {tiposGastos.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Validado</Form.Label>
                    <Form.Control
                      value={gastoToEdit.VALIDADO ? "Sí" : "No"}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleEditConfirm}
            disabled={tableLoading}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación para exportar con gastos no aprobados */}
      <Modal
        show={showExportConfirmModal}
        onHide={() => setShowExportConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exportación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <FaFileExcel
                className="text-warning"
                style={{ fontSize: "3rem" }}
              />
            </div>
            <h5>¡Atención!</h5>
            <p>
              Se encontraron <strong>{exportData?.gastosNoAprobados}</strong>{" "}
              gastos no aprobados de un total de{" "}
              <strong>{exportData?.totalGastos}</strong> gastos en el rango de
              fechas seleccionado.
            </p>
            <p className="text-warning">
              <strong>¿Desea continuar con la exportación?</strong>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowExportConfirmModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="warning"
            onClick={handleExportConfirm}
            disabled={tableLoading}
          >
            Continuar Exportación
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GastosList;
